import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  AlertTriangle,
  BellRing,
  CalendarClock,
  CheckCircle2,
  Clock,
  Download,
  Eye,
  FileDigit,
  FileEdit,
  FilePlus,
  FileText,
  Layers,
  Pencil,
  Printer,
  Search,
  ShieldCheck,
  Trash2,
  XCircle,
} from 'lucide-react';

import { Pagination } from '../components/Pagination';
import {
  controlerLot,
  lireControle,
  lireRapports,
  lireReferencesRapports,
  lireVierges,
  supprimerRapport,
  urlExport,
  urlDocument,
  validerRapport,
  type ActionRapport,
  type PageRapports,
  type PageVierges,
  relancerLot,
  relancerVierge,
  type PeriodeRapport,
  type Rapport,
  type RapportVierge,
  type ReferencesRapport,
  type StatutRapport,
} from '../api/rapports';
import { ModaleRapport } from './ModaleRapport';
import { ModaleChoixIntervention } from './ModaleChoixIntervention';
import { ModaleRejet } from './ModaleRejet';

type Onglet = 'effectues' | 'vierges' | 'controle';

/** Délai avant d'interroger le serveur sur une recherche en cours de frappe. */
const DELAI_RECHERCHE_MS = 300;

/** Pastille d'un statut de rapport : même jeu de tonalités que le registre. */
const TONS: Record<StatutRapport, { libelle: string; ton: string }> = {
  brouillon: { libelle: 'Brouillon', ton: 'spill-unknown' },
  'en-attente': { libelle: 'En attente', ton: 'spill-info' },
  valide: { libelle: 'Validé', ton: 'spill-ok' },
  rejete: { libelle: 'Rejeté', ton: 'spill-danger' },
};

export interface Etat<T> {
  readonly donnees?: T;
  readonly chargement: boolean;
  readonly erreur?: string;
}

interface Modale {
  readonly mode: 'voir' | 'editer';
  readonly rapport?: Rapport;
  readonly vierge?: RapportVierge;
}

/**
 * « Mes rapports » : les comptes rendus d'intervention, en trois onglets — les
 * comptes rendus, les interventions qui en attendent un, et (pour une
 * administration) la file de contrôle de tous les portefeuilles.
 *
 * L'état de l'écran vit dans l'ADRESSE : onglet, recherche, filtre, tri, page et
 * taille survivent au rechargement et se partagent par un lien. Tout ce qui se
 * décide est demandé au serveur — l'échéance, le retard, les indicateurs, l'ordre,
 * les actions autorisées et jusqu'aux libellés. L'écran affiche, il ne calcule rien.
 */
export function Rapports() {
  const [parametres, setParametres] = useSearchParams();
  const onglet = (parametres.get('onglet') ?? 'effectues') as Onglet;
  const rechercheUrl = parametres.get('recherche') ?? '';
  const statut = parametres.get('statut') ?? '';
  const tri = parametres.get('tri') ?? 'date';
  const sens = parametres.get('sens') ?? '';
  const page = Number(parametres.get('page') ?? 1);
  const tailleUrl = parametres.get('taille') ?? '';
  const retardSeulement = parametres.get('retard') === '1';
  const periode = (parametres.get('periode') ?? 'tout') as PeriodeRapport;

  const [saisie, setSaisie] = useState(rechercheUrl);
  const [references, setReferences] = useState<ReferencesRapport>();
  const [rechargement, setRechargement] = useState(0);
  const [action, setAction] = useState<string>();
  const [aConfirmer, setAConfirmer] = useState<string>();
  const [effectues, setEffectues] = useState<Etat<PageRapports>>({ chargement: true });
  const [vierges, setVierges] = useState<Etat<PageVierges>>({ chargement: true });
  const [controle, setControle] = useState<Etat<PageRapports>>({ chargement: false });
  const [modale, setModale] = useState<Modale>();
  const [rejets, setRejets] = useState<Rapport[]>([]);
  const [selection, setSelection] = useState<string[]>([]);
  const [resultatLot, setResultatLot] = useState<string>();
  const [selectionVierges, setSelectionVierges] = useState<string[]>([]);
  const [relance, setRelance] = useState<string>();
  const [resultatRelance, setResultatRelance] = useState<string>();
  const [choixIntervention, setChoixIntervention] = useState(false);
  const premierOnglet = useRef<HTMLButtonElement>(null);

  /** Met à jour l'adresse : une COPIE, sinon React Router ne rend pas. */
  const majParametres = useCallback(
    (changements: Record<string, string | null>, garderPage = false) => {
      const suivant = new URLSearchParams(parametres);
      for (const [cle, valeur] of Object.entries(changements)) {
        if (valeur === null || valeur === '') suivant.delete(cle);
        else suivant.set(cle, valeur);
      }
      if (!garderPage) suivant.delete('page');
      setParametres(suivant, { replace: true });
    },
    [parametres, setParametres],
  );

  // La frappe est LOCALE : une requête par caractère ferait six appels pour six
  // lettres, là où les autres écrans du portail attendent 300 ms.
  useEffect(() => {
    if (saisie.trim() === rechercheUrl.trim()) return;
    const minuteur = globalThis.setTimeout(
      () => majParametres({ recherche: saisie.trim() }),
      DELAI_RECHERCHE_MS,
    );
    return () => globalThis.clearTimeout(minuteur);
  }, [saisie, rechercheUrl, majParametres]);

  useEffect(() => {
    if (!parametres.get('taille') || references === undefined) return;
    const proposees = references.tailles.map(String);
    if (!proposees.includes(parametres.get('taille') ?? '')) {
      majParametres({ taille: null }, true);
    }
  }, [parametres, references, majParametres]);

  useEffect(() => {
    const controleur = new AbortController();
    void lireReferencesRapports({ signal: controleur.signal })
      .then((resultat) => {
        if (resultat.ok && resultat.donnees) setReferences(resultat.donnees);
      })
      .catch(() => {
        /* abandon : écran quitté */
      });
    return () => controleur.abort();
  }, []);

  const criteres = {
    ...(rechercheUrl ? { recherche: rechercheUrl } : {}),
    ...(statut ? { statut } : {}),
    periode,
    tri,
    ...(sens ? { sens } : {}),
    page,
    ...(tailleUrl ? { taille: Number(tailleUrl) } : {}),
  };

  // Une clé PRIMITIVE pour les dépendances : un objet de critères recréé à chaque
  // rendu relancerait les requêtes en boucle.
  const cleCriteres = `${rechercheUrl}|${statut}|${tri}|${sens}|${page}|${tailleUrl}|${periode}`;

  // Les deux onglets de travail sont chargés ensemble : la pastille de l'onglet
  // « vierges » annonce un volume, faux tant qu'on ne l'a pas ouvert.
  useEffect(() => {
    const controleur = new AbortController();
    setEffectues((precedent) => ({ ...precedent, chargement: true, erreur: undefined }));
    void lireRapports(criteres, { signal: controleur.signal }).then((resultat) => {
      if (resultat.ok && resultat.donnees) {
        setEffectues({ donnees: resultat.donnees, chargement: false });
      } else {
        setEffectues({ chargement: false, erreur: resultat.message ?? 'Erreur inattendue.' });
      }
    });
    return () => controleur.abort();
  }, [cleCriteres, rechargement]);

  useEffect(() => {
    const controleur = new AbortController();
    setVierges((precedent) => ({ ...precedent, chargement: true, erreur: undefined }));
    void lireVierges(
      {
        ...(rechercheUrl ? { recherche: rechercheUrl } : {}),
        retard: retardSeulement,
        periode,
        page,
        ...(tailleUrl ? { taille: Number(tailleUrl) } : {}),
      },
      { signal: controleur.signal },
    ).then((resultat) => {
      if (resultat.ok && resultat.donnees) {
        setVierges({ donnees: resultat.donnees, chargement: false });
      } else {
        setVierges({ chargement: false, erreur: resultat.message ?? 'Erreur inattendue.' });
      }
    });
    return () => controleur.abort();
  }, [cleCriteres, retardSeulement, rechargement]);

  useEffect(() => {
    // La file de contrôle n'existe que pour une administration : on ne demande
    // pas une liste que le serveur refuserait (403).
    if (!references?.controle) return;
    const controleur = new AbortController();
    setControle((precedent) => ({ ...precedent, chargement: true, erreur: undefined }));
    void lireControle(criteres, { signal: controleur.signal }).then((resultat) => {
      if (resultat.ok && resultat.donnees) {
        setControle({ donnees: resultat.donnees, chargement: false });
      } else {
        setControle({ chargement: false, erreur: resultat.message ?? 'Erreur inattendue.' });
      }
    });
    return () => controleur.abort();
  }, [cleCriteres, references?.controle, rechargement]);

  const effacerFiltres = () => {
    setSaisie('');
    majParametres({
      recherche: null,
      statut: null,
      retard: null,
      tri: null,
      sens: null,
      periode: null,
    });
  };

  const apresEcriture = () => {
    // Les trois onglets bougent : un brouillon créé sort l'intervention des
    // « vierges », entre dans les « effectués » et dans la file de contrôle.
    setRechargement((valeur) => valeur + 1);
  };

  const executer = async (nom: string, appel: () => Promise<{ ok: boolean; message?: string }>) => {
    setAction(nom);
    const resultat = await appel();
    setAction(undefined);
    if (!resultat.ok) {
      setEffectues((precedent) => ({
        ...precedent,
        erreur: resultat.message ?? 'Action impossible.',
      }));
      return;
    }
    apresEcriture();
  };

  /** Contrôle groupé : le serveur rend le sort de CHAQUE identifiant. */
  const controlerSelection = async (decision: 'valider' | 'rejeter') => {
    if (selection.length === 0) return;
    if (decision === 'rejeter') {
      const choisis = (controle.donnees?.items ?? []).filter((rapport) =>
        selection.includes(rapport.id),
      );
      setRejets(choisis);
      return;
    }
    setAction('lot');
    const resultat = await controlerLot(selection, 'valider');
    setAction(undefined);
    if (!resultat.ok || !resultat.donnees) {
      setResultatLot(resultat.message ?? 'Contrôle impossible.');
      return;
    }
    // Le compte rendu vient du serveur : nombre faits / refusés, et le motif de
    // chaque refus. L'écran n'additionne rien.
    const refus = resultat.donnees.resultats.filter((ligne) => !ligne.ok);
    setResultatLot(
      `${resultat.donnees.faits} compte(s) rendu(s) validé(s)` +
        (refus.length > 0
          ? ` · ${resultat.donnees.refuses} refusé(s) : ${refus
              .map((ligne) => ligne.message)
              .join(' ; ')}`
          : ''),
    );
    setSelection([]);
    apresEcriture();
  };

  /** Relance UNE intervention : le serveur décide, l'écran affiche son verdict. */
  const relancerUne = async (vierge: RapportVierge) => {
    setRelance(vierge.interventionId);
    setResultatRelance(undefined);
    const resultat = await relancerVierge(vierge.interventionId);
    setRelance(undefined);
    if (!resultat.ok || !resultat.donnees) {
      setResultatRelance(resultat.message ?? 'Relance impossible.');
      return;
    }
    setResultatRelance(
      `Rappel envoyé${resultat.donnees.technicien ? ` à ${resultat.donnees.technicien}` : ''} pour ${resultat.donnees.client}.`,
    );
    apresEcriture();
  };

  /** Relance GROUPÉE : le serveur rend le sort de chaque intervention. */
  const relancerSelection = async () => {
    if (selectionVierges.length === 0) return;
    setRelance('lot');
    setResultatRelance(undefined);
    const resultat = await relancerLot(selectionVierges);
    setRelance(undefined);
    if (!resultat.ok || !resultat.donnees) {
      setResultatRelance(resultat.message ?? 'Relance impossible.');
      return;
    }
    const refus = resultat.donnees.resultats.filter((ligne) => !ligne.ok);
    setResultatRelance(
      `${resultat.donnees.faits} rappel(s) envoyé(s)` +
        (refus.length > 0
          ? ` · ${resultat.donnees.refuses} refusé(s) : ${refus
              .map((ligne) => ligne.message)
              .join(' ; ')}`
          : ''),
    );
    setSelectionVierges([]);
    apresEcriture();
  };

  const trier = (code: string) => {
    if (tri === code) {
      const defaut = references?.tris.find((entree) => entree.code === code)?.sensDefaut ?? 'desc';
      majParametres({
        tri: code,
        sens: sens === defaut ? (defaut === 'asc' ? 'desc' : 'asc') : defaut,
      });
      return;
    }
    const defaut = references?.tris.find((entree) => entree.code === code)?.sensDefaut ?? 'desc';
    majParametres({ tri: code, sens: defaut });
  };

  const compteursVierges = vierges.donnees?.compteurs;
  const compteursRapports = effectues.donnees?.compteurs;
  const filtresActifs =
    rechercheUrl !== '' || statut !== '' || retardSeulement || periode !== 'tout';
  const controles =
    onglet === 'controle'
      ? controle
      : { donnees: controle.donnees, chargement: controle.chargement };

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-600">
        Vos comptes rendus d’intervention, et les interventions terminées qui attendent encore le
        leur. Un compte rendu se remplit dans les{' '}
        {references ? `${references.delaiSaisieJours} jours` : 'délai'} qui suivent le passage.
      </p>

      <div
        role="tablist"
        aria-label="Rapports"
        className="flex flex-wrap items-center gap-2 border-b border-slate-200"
      >
        <BoutonOnglet
          reference={premierOnglet}
          id="onglet-effectues"
          actif={onglet === 'effectues'}
          libelle="Rapports effectués"
          icone={<FileText className="w-4 h-4" aria-hidden="true" />}
          onChoisir={() => majParametres({ onglet: 'effectues' })}
          onSuivant={() => majParametres({ onglet: 'vierges' })}
        />
        <BoutonOnglet
          id="onglet-vierges"
          actif={onglet === 'vierges'}
          libelle="Rapports vierges"
          icone={<FileEdit className="w-4 h-4" aria-hidden="true" />}
          pastille={compteursVierges?.total}
          onChoisir={() => majParametres({ onglet: 'vierges' })}
          onPrecedent={() => majParametres({ onglet: 'effectues' })}
          onSuivant={references?.controle ? () => majParametres({ onglet: 'controle' }) : undefined}
        />
        {references?.controle && (
          <BoutonOnglet
            id="onglet-controle"
            actif={onglet === 'controle'}
            libelle="À contrôler"
            icone={<ShieldCheck className="w-4 h-4" aria-hidden="true" />}
            pastille={controle.donnees?.compteurs.enAttente}
            onChoisir={() => majParametres({ onglet: 'controle' })}
            onPrecedent={() => majParametres({ onglet: 'vierges' })}
          />
        )}
      </div>

      <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
        <div className="relative w-full sm:w-72">
          <label htmlFor="recherche-rapports" className="sr-only">
            Rechercher un compte rendu ou une intervention
          </label>
          <Search
            className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
            aria-hidden="true"
          />
          <input
            id="recherche-rapports"
            type="search"
            value={saisie}
            onChange={(evenement) => setSaisie(evenement.target.value)}
            placeholder={
              onglet === 'vierges' ? 'Rechercher une intervention…' : 'Rechercher un compte rendu…'
            }
            className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>

        {/* Période : liste FERMÉE servie par le serveur, qui en calcule les bornes. */}
        <div className="flex items-center gap-2">
          <label htmlFor="filtre-periode" className="sr-only">
            Filtrer par période
          </label>
          <select
            id="filtre-periode"
            value={periode}
            onChange={(evenement) => majParametres({ periode: evenement.target.value })}
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            {(references?.periodes ?? []).map((entree) => (
              <option key={entree.code} value={entree.code}>
                {entree.libelle}
              </option>
            ))}
          </select>
        </div>

        {onglet !== 'vierges' && (
          <>
            <div className="flex items-center gap-2">
              <label htmlFor="filtre-statut-rapport" className="sr-only">
                Filtrer par statut
              </label>
              <select
                id="filtre-statut-rapport"
                value={statut}
                onChange={(evenement) => majParametres({ statut: evenement.target.value })}
                className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="">
                  {onglet === 'controle' ? 'En attente de contrôle' : 'Tous les statuts'}
                </option>
                {(references?.statuts ?? []).map((entree) => (
                  <option key={entree.code} value={entree.code}>
                    {entree.libelle}
                  </option>
                ))}
              </select>
            </div>

            {/* Export CSV : le serveur le fabrique, la liste filtrée entière. */}
            <a
              href={urlExport(criteres)}
              className="px-3 py-2 text-sm text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 inline-flex items-center gap-2"
              title="Télécharger la liste filtrée au format CSV"
            >
              <Download className="w-4 h-4" aria-hidden="true" /> Exporter
            </a>
          </>
        )}

        {onglet === 'vierges' && (
          <label className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700">
            <input
              type="checkbox"
              checked={retardSeulement}
              onChange={(evenement) =>
                majParametres({ retard: evenement.target.checked ? '1' : null })
              }
              className="w-4 h-4 accent-red-600"
            />
            En retard seulement
          </label>
        )}

        {/* Créer un compte rendu : la modale ne s'ouvre que s'il y a une
            intervention à couvrir — le serveur en décide, pas l'écran. */}
        <button
          type="button"
          onClick={() => setChoixIntervention(true)}
          className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 inline-flex items-center gap-2"
        >
          <FilePlus className="w-4 h-4" aria-hidden="true" /> Créer un nouveau rapport
        </button>

        <div className="flex items-center gap-2 ml-auto">
          <label htmlFor="taille-page" className="text-sm text-slate-600">
            Par page
          </label>
          <select
            id="taille-page"
            value={tailleUrl}
            onChange={(evenement) => majParametres({ taille: evenement.target.value })}
            className="px-2 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="">Défaut</option>
            {(references?.tailles ?? []).map((valeur) => (
              <option key={valeur} value={valeur}>
                {valeur}
              </option>
            ))}
          </select>
        </div>

        {filtresActifs && (
          <button
            type="button"
            onClick={effacerFiltres}
            className="px-3 py-2 text-sm text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            Effacer les filtres
          </button>
        )}
      </div>

      {/* Retour des actions (validation, rejet, suppression) : jamais muet. */}
      {action && (
        <p role="status" className="text-sm text-slate-600 flex items-center gap-2">
          <Clock className="w-4 h-4 animate-spin" aria-hidden="true" /> Traitement en cours…
        </p>
      )}
      {effectues.erreur && (
        <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800 mb-2">{effectues.erreur}</p>
          {/* Une panne n'est pas une impasse : on peut redemander les listes. */}
          <button
            type="button"
            onClick={apresEcriture}
            className="px-3 py-1.5 text-sm text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            Réessayer
          </button>
        </div>
      )}

      {onglet === 'effectues' && (
        <div
          role="tabpanel"
          id="panneau-effectues"
          aria-labelledby="onglet-effectues"
          className="space-y-6"
        >
          <div
            role="group"
            aria-label="Indicateurs des rapports"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
          >
            <CarteIndicateur
              libelle="En attente"
              valeur={compteursRapports?.enAttente}
              bordure="border-l-blue-500"
              icone={<Clock className="w-4 h-4" aria-hidden="true" />}
              ton="bg-blue-50 text-blue-600"
              actif={statut === 'en-attente'}
              onClick={() => majParametres({ statut: 'en-attente' })}
            />
            <CarteIndicateur
              libelle="Validés"
              valeur={compteursRapports?.valides}
              bordure="border-l-emerald-500"
              icone={<CheckCircle2 className="w-4 h-4" aria-hidden="true" />}
              ton="bg-emerald-50 text-emerald-600"
              actif={statut === 'valide'}
              onClick={() => majParametres({ statut: 'valide' })}
            />
            <CarteIndicateur
              libelle="Brouillons"
              valeur={compteursRapports?.brouillons}
              bordure="border-l-slate-400"
              icone={<FileEdit className="w-4 h-4" aria-hidden="true" />}
              ton="bg-slate-100 text-slate-600"
              actif={statut === 'brouillon'}
              onClick={() => majParametres({ statut: 'brouillon' })}
            />
            <CarteIndicateur
              libelle="Rejetés"
              valeur={compteursRapports?.rejetes}
              bordure="border-l-red-500"
              icone={<XCircle className="w-4 h-4" aria-hidden="true" />}
              ton="bg-red-50 text-red-600"
              actif={statut === 'rejete'}
              onClick={() => majParametres({ statut: 'rejete' })}
            />
          </div>

          <TableauRapports
            page={effectues.donnees}
            chargement={effectues.chargement}
            erreur={effectues.erreur}
            legende="Comptes rendus d’intervention, filtrés, triés et paginés par le serveur"
            vide={
              filtresActifs
                ? 'Aucun compte rendu ne correspond à ces critères.'
                : 'Aucun compte rendu pour le moment : remplissez celui d’une intervention terminée depuis l’onglet « Rapports vierges ».'
            }
            onEffacerFiltres={filtresActifs ? effacerFiltres : undefined}
            onPage={(valeur) => majParametres({ page: String(valeur) }, true)}
            tri={tri}
            sens={sens}
            onTrier={trier}
            action={action}
            aConfirmer={aConfirmer}
            onVoir={(rapport) => setModale({ mode: 'voir', rapport })}
            onEditer={(rapport) => setModale({ mode: 'editer', rapport })}
            onValider={(rapport) => void executer(rapport.id, () => validerRapport(rapport.id))}
            onRejeter={(rapport) => setRejets([rapport])}
            onSupprimer={(rapport) => {
              if (aConfirmer !== rapport.id) {
                setAConfirmer(rapport.id);
                return;
              }
              setAConfirmer(undefined);
              void executer(rapport.id, () => supprimerRapport(rapport.id));
            }}
          />
        </div>
      )}

      {onglet === 'vierges' && (
        <div
          role="tabpanel"
          id="panneau-vierges"
          aria-labelledby="onglet-vierges"
          className="space-y-6"
        >
          <div
            role="group"
            aria-label="Indicateurs des interventions à compléter"
            className="grid grid-cols-1 sm:grid-cols-3 gap-4"
          >
            <CarteIndicateur
              libelle="À remplir"
              valeur={compteursVierges?.aRemplir}
              bordure="border-l-amber-500"
              icone={<FileEdit className="w-4 h-4" aria-hidden="true" />}
              ton="bg-amber-50 text-amber-600"
              aide="Échéance non dépassée"
              actif={!retardSeulement}
              onClick={() => majParametres({ retard: null })}
            />
            <CarteIndicateur
              libelle="En retard de saisie"
              valeur={compteursVierges?.enRetard}
              bordure="border-l-red-500"
              icone={<AlertTriangle className="w-4 h-4" aria-hidden="true" />}
              ton="bg-red-50 text-red-600"
              aide={`Délai${references ? ` de ${references.delaiSaisieJours} jours` : ''} dépassé`}
              actif={retardSeulement}
              onClick={() => majParametres({ retard: '1' })}
            />
            <CarteIndicateur
              libelle="Total à traiter"
              valeur={compteursVierges?.total}
              bordure="border-l-slate-400"
              icone={<Layers className="w-4 h-4" aria-hidden="true" />}
              ton="bg-slate-100 text-slate-600"
            />
          </div>

          {resultatRelance && (
            <div role="status" className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-900">{resultatRelance}</p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-slate-600" role="status">
              {selectionVierges.length === 0
                ? 'Cochez des interventions pour relancer leurs techniciens ensemble.'
                : `${selectionVierges.length} intervention${selectionVierges.length === 1 ? '' : 's'} sélectionnée${selectionVierges.length === 1 ? '' : 's'}`}
            </p>
            <button
              type="button"
              onClick={() => void relancerSelection()}
              disabled={selectionVierges.length === 0 || relance === 'lot'}
              className="px-3 py-1.5 text-sm text-amber-900 bg-white border border-amber-300 rounded-lg hover:bg-amber-50 disabled:opacity-50"
            >
              Relancer la sélection
            </button>
            {selectionVierges.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectionVierges([])}
                className="px-3 py-1.5 text-sm text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Tout désélectionner
              </button>
            )}
          </div>

          <TableauVierges
            page={vierges.donnees}
            chargement={vierges.chargement}
            erreur={vierges.erreur}
            relance={relance}
            onRelancer={(vierge) => void relancerUne(vierge)}
            selection={{
              ids: selectionVierges,
              basculer: (id) =>
                setSelectionVierges((actuels) =>
                  actuels.includes(id)
                    ? actuels.filter((actuel) => actuel !== id)
                    : [...actuels, id],
                ),
              tout: (cocher) =>
                setSelectionVierges(
                  cocher ? (vierges.donnees?.items ?? []).map((v) => v.interventionId) : [],
                ),
            }}
            vide={
              rechercheUrl !== ''
                ? 'Aucune intervention ne correspond à cette recherche.'
                : retardSeulement
                  ? 'Aucun retard de saisie : tout ce qui est dû a été rempli dans les délais.'
                  : 'Aucune intervention n’attend de compte rendu. Tout est à jour.'
            }
            onEffacerFiltres={filtresActifs ? effacerFiltres : undefined}
            onPage={(valeur) => majParametres({ page: String(valeur) }, true)}
            onRemplir={(vierge) => setModale({ mode: 'editer', vierge })}
          />
        </div>
      )}

      {onglet === 'controle' && references?.controle && (
        <div
          role="tabpanel"
          id="panneau-controle"
          aria-labelledby="onglet-controle"
          className="space-y-6"
        >
          <p className="text-sm text-slate-600">
            Les comptes rendus transmis par TOUS les comptes, en attente de contrôle. Valider les
            fige ; rejeter exige un motif, que le prestataire verra.
          </p>

          {resultatLot && (
            <div role="status" className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">{resultatLot}</p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-slate-600" role="status">
              {selection.length === 0
                ? 'Cochez des comptes rendus pour les contrôler ensemble.'
                : `${selection.length} compte${selection.length === 1 ? '' : 's'} rendu${selection.length === 1 ? '' : 's'} sélectionné${selection.length === 1 ? '' : 's'}`}
            </p>
            <button
              type="button"
              onClick={() => void controlerSelection('valider')}
              disabled={selection.length === 0 || action === 'lot'}
              className="px-3 py-1.5 text-sm text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              Valider la sélection
            </button>
            <button
              type="button"
              onClick={() => void controlerSelection('rejeter')}
              disabled={selection.length === 0}
              className="px-3 py-1.5 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              Rejeter la sélection
            </button>
            {selection.length > 0 && (
              <button
                type="button"
                onClick={() => setSelection([])}
                className="px-3 py-1.5 text-sm text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Tout désélectionner
              </button>
            )}
          </div>
          <TableauRapports
            page={controles.donnees}
            chargement={controles.chargement}
            erreur={controle.erreur}
            legende="File de contrôle : comptes rendus en attente, tous portefeuilles"
            vide="Aucun compte rendu n’attend d’être contrôlé."
            selection={{
              ids: selection,
              basculer: (id) =>
                setSelection((actuels) =>
                  actuels.includes(id)
                    ? actuels.filter((actuel) => actuel !== id)
                    : [...actuels, id],
                ),
              tout: (cocher) =>
                setSelection(
                  cocher ? (controle.donnees?.items ?? []).map((rapport) => rapport.id) : [],
                ),
            }}
            onPage={(valeur) => majParametres({ page: String(valeur) }, true)}
            tri={tri}
            sens={sens}
            onTrier={trier}
            action={action}
            aConfirmer={aConfirmer}
            onVoir={(rapport) => setModale({ mode: 'voir', rapport })}
            onValider={(rapport) => void executer(rapport.id, () => validerRapport(rapport.id))}
            onRejeter={(rapport) => setRejets([rapport])}
          />
        </div>
      )}

      <ModaleRapport
        ouvert={modale !== undefined}
        mode={modale?.mode ?? 'voir'}
        {...(modale?.rapport ? { rapport: modale.rapport } : {})}
        {...(modale?.vierge ? { vierge: modale.vierge } : {})}
        onFermer={() => setModale(undefined)}
        onEnregistre={apresEcriture}
        onModifier={(rapport) => setModale({ mode: 'editer', rapport })}
      />

      <ModaleChoixIntervention
        ouvert={choixIntervention}
        page={vierges}
        onFermer={() => setChoixIntervention(false)}
        onChoisir={(vierge) => {
          setChoixIntervention(false);
          setModale({ mode: 'editer', vierge });
        }}
      />

      <ModaleRejet
        rapports={rejets}
        onFermer={() => setRejets([])}
        onRejete={() => {
          setSelection([]);
          apresEcriture();
        }}
      />
    </div>
  );
}

/** Onglet clavier : flèches gauche/droite, comme l'attend le motif ARIA. */
function BoutonOnglet({
  id,
  actif,
  libelle,
  icone,
  pastille,
  reference,
  onChoisir,
  onPrecedent,
  onSuivant,
}: {
  readonly id: string;
  readonly actif: boolean;
  readonly libelle: string;
  readonly icone: React.ReactNode;
  readonly pastille?: number;
  readonly reference?: React.RefObject<HTMLButtonElement | null>;
  readonly onChoisir: () => void;
  readonly onPrecedent?: () => void;
  readonly onSuivant?: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      id={id}
      ref={reference}
      aria-selected={actif}
      aria-controls={`panneau-${id.replace('onglet-', '')}`}
      tabIndex={actif ? 0 : -1}
      onClick={onChoisir}
      onKeyDown={(evenement) => {
        if (evenement.key === 'ArrowRight' && onSuivant) {
          evenement.preventDefault();
          onSuivant();
        }
        if (evenement.key === 'ArrowLeft' && onPrecedent) {
          evenement.preventDefault();
          onPrecedent();
        }
      }}
      className={`px-5 py-2.5 text-sm font-medium border-b-2 flex items-center gap-2 ${
        actif
          ? 'text-slate-900 border-slate-900'
          : 'text-slate-500 border-transparent hover:text-slate-800'
      }`}
    >
      {icone} {libelle}
      {pastille !== undefined && pastille > 0 && (
        <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full font-bold">
          {pastille}
        </span>
      )}
    </button>
  );
}

interface ProprietesTableau {
  readonly page?: PageRapports;
  readonly chargement: boolean;
  readonly erreur?: string;
  readonly legende: string;
  readonly vide: string;
  readonly onEffacerFiltres?: () => void;
  readonly onPage: (page: number) => void;
  readonly tri: string;
  readonly sens: string;
  readonly onTrier: (code: string) => void;
  readonly action?: string;
  readonly aConfirmer?: string;
  /** Sélection multiple (file de contrôle) : absente = pas de cases à cocher. */
  readonly selection?: {
    readonly ids: readonly string[];
    readonly basculer: (id: string) => void;
    readonly tout: (cocher: boolean) => void;
  };
  readonly onVoir: (rapport: Rapport) => void;
  readonly onEditer?: (rapport: Rapport) => void;
  readonly onValider?: (rapport: Rapport) => void;
  readonly onRejeter?: (rapport: Rapport) => void;
  readonly onSupprimer?: (rapport: Rapport) => void;
}

/** Le tableau des comptes rendus : une ligne par rapport, actions lues du serveur. */
function TableauRapports({
  page,
  chargement,
  erreur,
  legende,
  vide,
  onEffacerFiltres,
  onPage,
  tri,
  sens,
  onTrier,
  action,
  aConfirmer,
  selection,
  onVoir,
  onEditer,
  onValider,
  onRejeter,
  onSupprimer,
}: ProprietesTableau) {
  // `indeterminate` n'est pas un attribut HTML : il se pose par référence.
  const enteteSelection = useRef<HTMLInputElement>(null);
  const tousCoches =
    selection !== undefined &&
    page !== undefined &&
    page.items.length > 0 &&
    page.items.every((rapport) => selection.ids.includes(rapport.id));
  const certainsCoches =
    selection !== undefined &&
    !tousCoches &&
    (page?.items ?? []).some((rapport) => selection.ids.includes(rapport.id));
  useEffect(() => {
    if (enteteSelection.current) enteteSelection.current.indeterminate = certainsCoches;
  }, [certainsCoches]);
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      {!page && chargement && !erreur && (
        <p className="p-6 text-sm text-slate-500">Chargement des comptes rendus…</p>
      )}

      {page && !erreur && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <caption className="sr-only">{legende}</caption>
              <thead className="bg-slate-50 text-slate-600 font-medium border-b border-slate-200">
                <tr>
                  {selection && (
                    <th scope="col" className="px-4 py-4 w-10">
                      <input
                        ref={enteteSelection}
                        type="checkbox"
                        checked={tousCoches}
                        onChange={(evenement) => selection.tout(evenement.target.checked)}
                        aria-label="Sélectionner tous les comptes rendus de la page"
                        className="w-4 h-4 accent-red-600"
                      />
                    </th>
                  )}
                  <EnteteTri
                    code="date"
                    libelle="Date d’intervention"
                    {...{ tri, sens, onTrier }}
                  />
                  <EnteteTri code="client" libelle="Client" {...{ tri, sens, onTrier }} />
                  <th scope="col" className="px-6 py-4">
                    Type de compte rendu
                  </th>
                  <EnteteTri code="technicien" libelle="Assigné à" {...{ tri, sens, onTrier }} />
                  <EnteteTri code="statut" libelle="Statut" {...{ tri, sens, onTrier }} />
                  <EnteteTri
                    code="modifie"
                    libelle="Dernière modification"
                    {...{ tri, sens, onTrier }}
                  />
                  <th scope="col" className="px-6 py-4 text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {page.items.length === 0 && (
                  <tr>
                    <td colSpan={selection ? 8 : 7} className="px-6 py-10 text-center">
                      <p className="text-sm text-slate-600">{vide}</p>
                      {onEffacerFiltres && (
                        <button
                          type="button"
                          onClick={onEffacerFiltres}
                          className="mt-3 px-3 py-1.5 text-sm text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                        >
                          Effacer les filtres
                        </button>
                      )}
                    </td>
                  </tr>
                )}
                {page.items.map((rapport) => (
                  <tr key={rapport.id} className="hover:bg-slate-50">
                    {selection && (
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selection.ids.includes(rapport.id)}
                          onChange={() => selection.basculer(rapport.id)}
                          aria-label={`Sélectionner le compte rendu de ${rapport.client}, ${rapport.dateLisible}`}
                          className="w-4 h-4 accent-red-600"
                        />
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-slate-900">
                      {rapport.dateLisible}
                    </td>
                    <td className="px-6 py-4 text-slate-700">
                      {rapport.client}
                      <span className="block text-xs text-slate-500">{rapport.lieu}</span>
                    </td>
                    <td className="px-6 py-4 text-slate-700">{rapport.typeRapport}</td>
                    <td className="px-6 py-4">
                      {rapport.technicien ? (
                        <span className="flex items-center gap-2">
                          <span
                            aria-hidden="true"
                            className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-[10px]"
                          >
                            {rapport.technicienInitiales}
                          </span>
                          <span className="text-slate-700">{rapport.technicien}</span>
                        </span>
                      ) : (
                        <span className="text-slate-500">Non affecté</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`spill ${TONS[rapport.statut].ton}`}>
                        {TONS[rapport.statut].libelle}
                      </span>
                      {rapport.motifRejet && (
                        <span className="block text-xs text-red-700 mt-1 max-w-xs">
                          {rapport.motifRejet}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-500">
                      <time dateTime={rapport.modifieLe}>{rapport.modifieLisible}</time>
                    </td>
                    <td className="px-6 py-4">
                      <ActionsRapport
                        rapport={rapport}
                        action={action}
                        aConfirmer={aConfirmer}
                        onVoir={onVoir}
                        {...(onEditer ? { onEditer } : {})}
                        {...(onValider ? { onValider } : {})}
                        {...(onRejeter ? { onRejeter } : {})}
                        {...(onSupprimer ? { onSupprimer } : {})}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center gap-3 px-6 py-4 border-t border-slate-100">
            <p className="text-sm text-slate-500">
              {page.total} compte{page.total === 1 ? '' : 's'} rendu
              {page.total === 1 ? '' : 's'} au total
            </p>
            <Pagination page={page.page} pages={page.pages} onChange={onPage} />
          </div>
        </>
      )}
    </div>
  );
}

/** En-tête de colonne triable : `aria-sort` porte l'ordre courant. */
function EnteteTri({
  code,
  libelle,
  tri,
  sens,
  onTrier,
}: {
  readonly code: string;
  readonly libelle: string;
  readonly tri: string;
  readonly sens: string;
  readonly onTrier: (code: string) => void;
}) {
  const actif = tri === code;
  const ariaSort = actif ? (sens === 'asc' ? 'ascending' : 'descending') : 'none';
  return (
    <th scope="col" aria-sort={ariaSort} className="px-6 py-4">
      <button
        type="button"
        onClick={() => onTrier(code)}
        className="inline-flex items-center gap-1 hover:text-slate-900"
      >
        {libelle}
        <span aria-hidden="true">{actif ? (sens === 'asc' ? '↑' : '↓') : '↕'}</span>
      </button>
    </th>
  );
}

/** Les actions autorisées pour CE rapport, dans l'ordre décidé par le serveur. */
function ActionsRapport({
  rapport,
  action,
  aConfirmer,
  onVoir,
  onEditer,
  onValider,
  onRejeter,
  onSupprimer,
}: {
  readonly rapport: Rapport;
  readonly action?: string;
  readonly aConfirmer?: string;
  /** Sélection multiple (file de contrôle) : absente = pas de cases à cocher. */
  readonly selection?: {
    readonly ids: readonly string[];
    readonly basculer: (id: string) => void;
    readonly tout: (cocher: boolean) => void;
  };
  readonly onVoir: (rapport: Rapport) => void;
  readonly onEditer?: (rapport: Rapport) => void;
  readonly onValider?: (rapport: Rapport) => void;
  readonly onRejeter?: (rapport: Rapport) => void;
  readonly onSupprimer?: (rapport: Rapport) => void;
}) {
  const occupe = action === rapport.id;
  const confirmation = aConfirmer === rapport.id;

  const rendre = (entree: ActionRapport) => {
    const commun = {
      key: entree.code,
      type: 'button' as const,
      title: entree.libelle,
      'aria-label': `${entree.libelle} — ${rapport.client}, ${rapport.dateLisible}`,
      disabled: occupe,
      className:
        'p-1.5 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100 disabled:opacity-50',
    };
    switch (entree.code) {
      case 'voir':
        return (
          <button {...commun} onClick={() => onVoir(rapport)}>
            <Eye className="w-4 h-4" aria-hidden="true" />
          </button>
        );
      case 'telecharger':
        return (
          <a
            key={entree.code}
            href={urlDocument(rapport.id)}
            target="_blank"
            rel="noopener noreferrer"
            title={entree.libelle}
            aria-label={`${entree.libelle} — ${rapport.client}, ${rapport.dateLisible}`}
            className="p-1.5 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100 inline-flex"
          >
            <Printer className="w-4 h-4" aria-hidden="true" />
          </a>
        );
      case 'modifier':
        return (
          <button {...commun} onClick={() => onEditer?.(rapport)}>
            <Pencil className="w-4 h-4" aria-hidden="true" />
          </button>
        );
      case 'valider':
        return (
          <button
            {...commun}
            className="p-1.5 rounded-md text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
            onClick={() => onValider?.(rapport)}
          >
            <CheckCircle2 className="w-4 h-4" aria-hidden="true" />
          </button>
        );
      case 'rejeter':
        return (
          <button
            {...commun}
            className="p-1.5 rounded-md text-red-600 hover:text-red-800 hover:bg-red-50 disabled:opacity-50"
            onClick={() => onRejeter?.(rapport)}
          >
            <XCircle className="w-4 h-4" aria-hidden="true" />
          </button>
        );
      case 'supprimer':
        return (
          <button
            {...commun}
            className={`p-1.5 rounded-md disabled:opacity-50 ${
              confirmation
                ? 'text-white bg-red-600 hover:bg-red-700'
                : 'text-slate-500 hover:text-red-700 hover:bg-red-50'
            }`}
            title={confirmation ? 'Cliquez à nouveau pour confirmer' : entree.libelle}
            aria-label={
              confirmation
                ? `Confirmer la suppression du brouillon de ${rapport.client}`
                : `${entree.libelle} — ${rapport.client}, ${rapport.dateLisible}`
            }
            onClick={() => onSupprimer?.(rapport)}
          >
            {confirmation ? (
              <span className="text-xs font-medium px-1">Confirmer</span>
            ) : (
              <Trash2 className="w-4 h-4" aria-hidden="true" />
            )}
          </button>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex items-center justify-end gap-1">
      {rapport.actions.map((entree) => rendre(entree))}
    </div>
  );
}

function TableauVierges({
  page,
  chargement,
  erreur,
  vide,
  onEffacerFiltres,
  onPage,
  onRemplir,
  onRelancer,
  relance,
  selection,
}: {
  readonly page?: PageVierges;
  readonly chargement: boolean;
  readonly erreur?: string;
  readonly vide: string;
  readonly onEffacerFiltres?: () => void;
  readonly onPage: (page: number) => void;
  readonly onRemplir: (vierge: RapportVierge) => void;
  readonly onRelancer: (vierge: RapportVierge) => void;
  readonly relance?: string;
  /** Sélection multiple : relancer plusieurs interventions d'un geste. */
  readonly selection: {
    readonly ids: readonly string[];
    readonly basculer: (id: string) => void;
    readonly tout: (cocher: boolean) => void;
  };
}) {
  const enteteSelection = useRef<HTMLInputElement>(null);
  const tousCoches =
    page !== undefined &&
    page.items.length > 0 &&
    page.items.every((vierge) => selection.ids.includes(vierge.interventionId));
  const certainsCoches =
    !tousCoches &&
    (page?.items ?? []).some((vierge) => selection.ids.includes(vierge.interventionId));
  useEffect(() => {
    if (enteteSelection.current) enteteSelection.current.indeterminate = certainsCoches;
  }, [certainsCoches]);
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      {erreur && (
        <div role="alert" className="p-6">
          <p className="font-medium text-red-700 mb-1">Interventions à compléter indisponibles</p>
          <p className="text-sm text-slate-600">{erreur}</p>
        </div>
      )}

      {!page && chargement && !erreur && (
        <p className="p-6 text-sm text-slate-500">Chargement des interventions…</p>
      )}

      {page && !erreur && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <caption className="sr-only">
                Interventions terminées sans compte rendu, les plus urgentes d’abord
              </caption>
              <thead className="bg-slate-50 text-slate-600 font-medium border-b border-slate-200">
                <tr>
                  <th scope="col" className="px-4 py-4 w-10">
                    <input
                      ref={enteteSelection}
                      type="checkbox"
                      checked={tousCoches}
                      onChange={(evenement) => selection.tout(evenement.target.checked)}
                      aria-label="Sélectionner toutes les interventions de la page"
                      className="w-4 h-4 accent-red-600"
                    />
                  </th>
                  <th scope="col" className="px-6 py-4">
                    Échéance
                  </th>
                  <th scope="col" className="px-6 py-4">
                    Client &amp; lieu
                  </th>
                  <th scope="col" className="px-6 py-4">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-4">
                    Assigné à
                  </th>
                  <th scope="col" className="px-6 py-4 text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {page.items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-10 text-center">
                      <p className="text-sm text-slate-600">{vide}</p>
                      {onEffacerFiltres && (
                        <button
                          type="button"
                          onClick={onEffacerFiltres}
                          className="mt-3 px-3 py-1.5 text-sm text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                        >
                          Effacer les filtres
                        </button>
                      )}
                    </td>
                  </tr>
                )}
                {page.items.map((vierge) => (
                  <tr
                    key={vierge.interventionId}
                    className={
                      vierge.enRetard ? 'bg-red-50/30 hover:bg-red-50/60' : 'hover:bg-slate-50'
                    }
                  >
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selection.ids.includes(vierge.interventionId)}
                        onChange={() => selection.basculer(vierge.interventionId)}
                        aria-label={`Sélectionner l’intervention de ${vierge.client}, ${vierge.dateLisible}`}
                        className="w-4 h-4 accent-red-600"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`spill ${vierge.enRetard ? 'spill-danger' : 'spill-surveil'}`}
                      >
                        <CalendarClock className="w-3 h-3" aria-hidden="true" />
                        {vierge.echeanceLisible}
                      </span>
                      <span className="block text-xs text-slate-500 mt-1">
                        Intervention du {vierge.dateLisible}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="block font-medium text-slate-800">{vierge.client}</span>
                      <span className="block text-xs text-slate-500">
                        {vierge.lieu || 'Lieu à préciser'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-700">{vierge.typeIntervention}</td>
                    <td className="px-6 py-4">
                      {vierge.technicien ? (
                        <span className="flex items-center gap-2">
                          <span
                            aria-hidden="true"
                            className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-[10px]"
                          >
                            {vierge.technicienInitiales}
                          </span>
                          <span className="text-slate-700">{vierge.technicien}</span>
                        </span>
                      ) : (
                        <span className="text-slate-500">Non affecté</span>
                      )}
                      {vierge.relanceLisible && (
                        <span className="block text-xs text-slate-500 mt-1">
                          {vierge.relanceLisible}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      {/* Pas de bouton mort : le serveur dit si une relance peut
                          partir maintenant, et sinon pourquoi (24 h, plafond). */}
                      <button
                        type="button"
                        onClick={() => onRelancer(vierge)}
                        disabled={!vierge.relancePossible || relance === vierge.interventionId}
                        title={vierge.relanceLibelle}
                        className="p-1.5 text-amber-700 border border-amber-300 rounded-lg hover:bg-amber-50 disabled:opacity-40 disabled:hover:bg-transparent"
                      >
                        <BellRing className="w-4 h-4" aria-hidden="true" />
                        <span className="sr-only">{vierge.relanceLibelle}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => onRemplir(vierge)}
                        className="ml-2 px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 inline-flex items-center gap-1.5"
                      >
                        <FileDigit className="w-3.5 h-3.5" aria-hidden="true" />
                        {vierge.actionLibelle}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center gap-3 px-6 py-4 border-t border-slate-100">
            <p className="text-sm text-slate-500">
              {page.total} intervention{page.total === 1 ? '' : 's'} à traiter
            </p>
            <Pagination page={page.page} pages={page.pages} onChange={onPage} />
          </div>
        </>
      )}
    </div>
  );
}

interface ProprietesCarte {
  readonly libelle: string;
  readonly valeur: number | undefined;
  readonly bordure: string;
  readonly icone: React.ReactNode;
  readonly ton: string;
  readonly aide?: string;
  /** Carte cliquable : elle filtre la liste sur ce statut. */
  readonly actif?: boolean;
  readonly onClick?: () => void;
}

/**
 * Carte-indicateur. Cliquable quand le serveur peut filtrer dessus : un chiffre
 * sur lequel on ne peut pas agir ressemble à un bouton sans en être un.
 */
function CarteIndicateur({
  libelle,
  valeur,
  bordure,
  icone,
  ton,
  aide,
  actif,
  onClick,
}: ProprietesCarte) {
  const contenu = (
    <div className="flex justify-between items-start">
      <div>
        <p className="text-xs font-semibold text-slate-600 uppercase">{libelle}</p>
        <p className="text-2xl font-bold text-slate-900">{valeur === undefined ? '—' : valeur}</p>
        {aide && <p className="text-[11px] text-slate-500 mt-1">{aide}</p>}
      </div>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${ton}`}>{icone}</div>
    </div>
  );

  const classes = `bg-white p-4 rounded-xl border border-slate-200 shadow-sm border-l-4 ${bordure} text-left`;

  if (!onClick) {
    return <div className={classes}>{contenu}</div>;
  }

  // Pas d'`aria-label` : le nom accessible doit contenir le texte VISIBLE
  // (WCAG 2.5.3). Le contenu « En attente 1 » suffit, `aria-pressed` dit que c'est
  // un filtre à bascule — un libellé qui réécrit le texte casse la correspondance
  // nom/texte, et axe le signale.
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={actif === true}
      title={`Filtrer sur « ${libelle} »`}
      className={`${classes} transition-colors hover:bg-slate-50 ${
        actif ? 'ring-2 ring-red-500' : ''
      }`}
    >
      {contenu}
    </button>
  );
}
